/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import Box from "../ui/Box";
import SpinnerLoader from "../ui/SpinnerLoader";

interface IEsimSalesGraphProps {
  data?: Array<{
    date: string;
    count: number;
  }>;
  loading?: boolean;
}

const EsimSalesGraph: FC<IEsimSalesGraphProps> = ({ data, loading }) => {
  const longestLabelLength =
    data
      ?.map((c) => c.count)
      .reduce(
        (acc, cur) =>
          cur.toString().length > acc ? cur.toString().length : acc,
        0
      ) || 1;

  return (
    <Box
      shadow="shadow-xl"
      padding="px-6 pt-6 pb-3.5"
      className="w-full h-full"
    >
      <div className="relative w-full h-full min-h-[400px]">
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data || []}>
            <defs>
              <linearGradient id="salesCount" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor="#3A81F6"
                  stopOpacity={0.5}
                  gradientTransform="180deg"
                />
                <stop offset="100%" stopColor="#3A81F6" stopOpacity={0} />
              </linearGradient>
            </defs>
            {data !== undefined && !loading && (
              <XAxis
                dataKey="date"
                axisLine={false}
                tickSize={0}
                tickFormatter={(date) => dayjs(date).format("DD/MM/YYYY")}
                interval="preserveStartEnd"
                fontSize={12}
                dy={16}
                color="#6E6E6E"
              />
            )}
            {data !== undefined && !loading && (
              <YAxis
                axisLine={false}
                allowDecimals={false}
                tickLine={false}
                dataKey="count"
                fontSize={12}
                color="#6E6E6E"
                width={longestLabelLength * 8}
              />
            )}
            <CartesianGrid
              strokeDasharray="4"
              stroke="#EAEAEA"
              vertical={false}
            />
            <Tooltip
              cursor={{
                stroke: "#2C2C2C",
                fill: "#2C2C2C",
                opacity: 0.5,
                strokeWidth: 1
              }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <Box
                      padding="px-4 py-3"
                      className="flex flex-col items-start justify-start text-sm
                      leading-4 text-black outline-none min-w-[150px]"
                    >
                      <span className="mb-2">
                        <span className="font-bold">
                          {payload[0].payload.count}
                        </span>{" "}
                        shitje
                      </span>
                      <span className="text-neutral">
                        {dayjs(payload[0].payload.date).format("MMMM DD, YYYY")}
                      </span>
                    </Box>
                  );
                }
                return null;
              }}
              wrapperStyle={{ outline: "none" }}
            />
            {data !== undefined && !loading && (
              <Area
                type="monotone"
                dataKey="count"
                stroke="#3A81F6"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#salesCount)"
              />
            )}
            <Legend
              verticalAlign="bottom"
              content={() => (
                <div className="flex justify-center items-center mt-3">
                  <div className="w-2 h-2 rounded-full bg-[#3A81F6] mr-2" />
                  <p className="text-sm font-medium leading-[18px]">
                    Numri i shitjeve
                  </p>
                </div>
              )}
            />
          </AreaChart>
        </ResponsiveContainer>
        {data === undefined && loading ? (
          <SpinnerLoader className="absolute top-0 w-full h-full flex justify-center items-center" />
        ) : null}
      </div>
    </Box>
  );
};

export default EsimSalesGraph;
